/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Input, OnInit, inject} from '@angular/core';

import template from './site-group-list-page.component.html';
import {
  SignalAdminListColumn,
  SignalAdminResourceListState,
  SignalAdminResourceListStateFactory
} from '../common/controller/signal-admin-resource-list-state';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {SiteApiService, SiteModel} from '../site/site-api.service';
import {SiteGroupApiService, SiteGroupModel} from './site-group-api.service';
import {UIRouterModule} from '@uirouter/angular';
import {AppSharedModule} from '../common/app-shared.module';
import {SignalAdminResourceListComponent} from '../common/controller/signal-admin-resource-list.component';

@Component({
  standalone: true,
  imports: [AppSharedModule, UIRouterModule, SignalAdminResourceListComponent],
  selector: 'adn-site-group-list-page',
  template
})
export class SiteGroupListPageComponent implements OnInit {
  readonly itemStateName = 'app.inv.site-groups.site-group';
  readonly siteItemStateName = 'app.inv.sites.site';
  readonly childColumns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    {param: 'siteUrl', labelInstant: 'Site URL'}
  ];
  readonly showOwnerColumn = inject(LocalNetworkProfile).isPureMarketplacePlatform();
  readonly columns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    ...(this.showOwnerColumn ? [{param: '', labelInstant: 'Owner', sortable: false}] : [])
  ];
  readonly list = inject(SignalAdminResourceListStateFactory).create<SiteGroupModel>(
    inject(SiteGroupApiService),
    {listId: 'fullSiteGroupList'},
    inject(DestroyRef)
  );

  private readonly listFactory = inject(SignalAdminResourceListStateFactory);
  private readonly siteApi = inject(SiteApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly children: Record<string, SignalAdminResourceListState<SiteModel>> = {};

  @Input()
  set readOnly(value: boolean) {
    this.list.readOnly.set(value);
  }

  ngOnInit(): void {
    this.list.load();
  }

  toggleChildren(siteGroup: SiteGroupModel): void {
    siteGroup.showChildren = !siteGroup.showChildren;
    if (!this.children[siteGroup.id]) {
      const childList = this.listFactory.create(
        this.siteApi,
        {listId: 'fullSiteList', baseTable: 'site', syncPageToUrl: false, additionalParams: {siteGroup: siteGroup.id}},
        this.destroyRef
      );
      childList.readOnly.set(true);
      this.children[siteGroup.id] = childList;
      childList.load();
    }
  }

  childList(siteGroupId: string): SignalAdminResourceListState<SiteModel> | undefined {
    return this.children[siteGroupId];
  }

  ownerLabel(siteGroup: SiteGroupModel): string {
    return siteGroup.ownerTeam?.name || 'Network';
  }
}
